// src/hooks/useScheduleNotifications.js
//
// ============================================================
// WHAT THIS FILE DOES
//
// Writes rows to the scheduled_notifications table after a gig
// is saved. A cron job running every minute on the server reads
// those rows and sends the actual push via OneSignal.
//
// TWO EXPORTED FUNCTIONS
//
//   scheduleForGig(gigId, gigData, userId)
//     Call this in AddGig right after every successful insert
//     or update. It calculates all the notification times for
//     that gig and writes one row per notification to the DB.
//
//   checkPendingNotifications()
//     This is a NO-OP now. It used to fire notifications
//     directly through the service worker, but that caused
//     duplicate pushes alongside the OneSignal cron. It is
//     kept as an empty function so the places that call it
//     in AddGig and Dashboard do not break.
//
// HOW NOTIFICATION TIMES ARE CALCULATED
//   Each notification time is derived from a time field on the
//   gig row (makeup_start_time, leave_time, etc.) converted
//   from the gig's local timezone to UTC.
//
//   "Warning" rows fire advanceNoticeMinutes before the exact
//   time. For example if advanceNoticeMinutes is 30, the
//   get_ready_warning fires 30 minutes before makeup_start_time.
//
// NOTIFICATION TYPES WRITTEN
//   get_ready_warning    -- fires advanceMin before makeup_start_time
//   get_ready_now        -- fires at makeup_start_time exactly
//   leave_home_warning   -- fires advanceMin before leave_home_time
//   leave_home_now       -- fires at leave_home_time (other mode only)
//   leave_warning        -- fires advanceMin before leave_time
//   leave_now            -- fires at leave_time exactly
//   pickup_warning       -- fires advanceMin before pickup_time
//   pickup_now           -- fires at pickup_time exactly
//   head_home_warning    -- fires early enough to drive home from venue
//   head_home_now        -- fires driveHomeMin before pickup_time
//
// PAST TIME HANDLING
//   If a notification time has already passed when the gig is
//   saved, the row gets send_at = now so the cron fires it on
//   the next run (within 1 minute).
//   Times more than STALE_HOURS (24 hours) in the past are
//   dropped entirely so old rows do not pile up.
// ============================================================

import { useState, useCallback } from 'react'
import { supabase } from '../lib/supabaseClient'

// Notifications older than this many hours are considered stale.
// Stale rows are dropped instead of scheduled for immediate delivery.
const STALE_HOURS = 24

// The public OSRM routing server. Used to calculate drive time
// from the venue back home for the head_home notification.
// Same service already used for leave_time routing in AddGig.
const OSRM_BASE = 'https://router.project-osrm.org/route/v1/driving'


// ============================================================
// gigLocalToUTC
//
// Converts a gig date + local time string from a named timezone
// into a UTC Date object.
//
// WHY NOT USE new Date(dateStr + 'T' + timeStr)
//   JavaScript's Date constructor interprets strings without a
//   timezone as LOCAL time, which changes based on the machine
//   running the code. On a server that runs in UTC, "2025-06-01
//   20:00" would be treated as 20:00 UTC instead of 20:00 Chicago
//   time. gigLocalToUTC uses Intl.DateTimeFormat to apply the
//   correct DST-aware offset for the given timezone.
//
// Returns a Date object in UTC, or null if anything fails.
// ============================================================
function gigLocalToUTC(dateStr, timeStr, timezone) {
  if (!dateStr || !timeStr || !timezone) return null
  // all three pieces are required -- missing any one means we
  // cannot calculate a meaningful UTC time

  try {
    const [year, month, day] = dateStr.split('-').map(Number)
    // dateStr = "2025-06-01" --> [2025, 6, 1]
    // .split('-') breaks it on hyphens --> ["2025", "06", "01"]
    // .map(Number) converts each string to a number

    const [hour, minute] = timeStr.split(':').map(Number)
    // timeStr = "20:00" --> [20, 0]

    // Build a UTC Date using the raw numbers.
    // This is a "nominal" UTC point -- it does not account for
    // the timezone yet, but we only use it as a reference to
    // ask Intl.DateTimeFormat what the local clock shows.
    const nominalUTC = new Date(Date.UTC(year, month - 1, day, hour, minute, 0))
    // month - 1 because JavaScript months are 0-indexed (Jan = 0)

    // Ask Intl what time this UTC moment looks like in the target timezone
    const formatter = new Intl.DateTimeFormat('en-CA', {
      timeZone: timezone,
      // the IANA timezone like 'America/Chicago'
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', hour12: false,
      // hour12: false = 24-hour clock so 8pm comes back as "20" not "08 PM"
    })

    // formatToParts returns an array of named pieces
    // e.g. [{ type: 'year', value: '2025' }, { type: 'month', value: '06' }, ...]
    const parts = {}
    formatter.formatToParts(nominalUTC).forEach(p => {
      if (p.type !== 'literal') parts[p.type] = p.value
      // 'literal' parts are separators like '-' or ',' -- skip them
    })

    // Build a UTC Date from what the formatter saw
    const seenHour = parseInt(parts.hour, 10) % 24
    // % 24 handles the edge case where hour comes back as "24" at midnight
    const formatterSaw = new Date(Date.UTC(
      parseInt(parts.year,   10),
      parseInt(parts.month,  10) - 1,
      // month - 1 because JS months start at 0
      parseInt(parts.day,    10),
      seenHour,
      parseInt(parts.minute, 10),
      0
    ))

    // The offset is the difference between what we passed in and
    // what the formatter saw in that timezone.
    // Adding this offset to nominalUTC gives the correct UTC time.
    const offsetMs = nominalUTC.getTime() - formatterSaw.getTime()
    return new Date(nominalUTC.getTime() + offsetMs)

  } catch (err) {
    console.error(
      '[useScheduleNotifications] gigLocalToUTC failed:', err.message,
      { dateStr, timeStr, timezone }
    )
    return null
  }
}


// ============================================================
// fetchDriveMinutes
//
// Calls the public OSRM routing API to get driving time in
// minutes between two coordinates.
//
// Used for the head_home notification -- we need to know how
// long it takes to drive from the venue to home so we can
// tell the performer exactly when to leave the show.
//
// Returns the drive time in minutes (rounded up), or null if
// the request fails or OSRM cannot find a route. Callers
// should skip the head_home rows when this returns null.
// ============================================================
async function fetchDriveMinutes(fromLat, fromLng, toLat, toLng) {
  try {
    // OSRM expects coordinates in lng,lat order (longitude first)
    // This is the opposite of most mapping APIs which use lat,lng
    const url = `${OSRM_BASE}/${fromLng},${fromLat};${toLng},${toLat}?overview=false`
    // overview=false = skip the detailed route geometry, we only need duration

    const res = await fetch(url)

    if (!res.ok) {
      console.warn('[useScheduleNotifications] OSRM returned non-OK status:', res.status)
      return null
    }

    const data = await res.json()

    if (data.code !== 'Ok' || !data.routes?.[0]?.duration) {
      // data.code !== 'Ok' = OSRM could not find a route
      // !data.routes?.[0]?.duration = no route data in the response
      console.warn('[useScheduleNotifications] OSRM response missing route:', data.code)
      return null
    }

    // OSRM returns duration in SECONDS. Convert to minutes and round up.
    // We round up so the performer always has a little extra time.
    return Math.ceil(data.routes[0].duration / 60)

  } catch (err) {
    console.warn('[useScheduleNotifications] OSRM fetch error:', err.message)
    return null
  }
}


// ============================================================
// useScheduleNotifications HOOK
// ============================================================

export function useScheduleNotifications() {

  const [scheduling, setScheduling] = useState(false)
  // true while scheduleForGig is running
  // used to show a loading indicator

  const [scheduleError, setScheduleError] = useState(null)
  // null = no problem
  // string = the last error message from scheduling


  // -------------------------------------------------------
  // checkPendingNotifications
  //
  // NO-OP. This function does nothing now.
  //
  // It used to fire notifications directly through the service
  // worker, but that caused duplicate notifications because
  // the OneSignal Edge Function cron was also sending them.
  // OneSignal handles all delivery now via the cron.
  //
  // The function is kept here as an empty stub so the places
  // that call it in AddGig and Dashboard do not need to change.
  // -------------------------------------------------------
  const checkPendingNotifications = useCallback(async () => {
    return 0
    // returns 0 to match the old return shape (count of sent notifications)
  }, [])


  // -------------------------------------------------------
  // scheduleForGig
  //
  // Writes rows to scheduled_notifications for a gig.
  // The Edge Function cron picks these up and sends them via
  // OneSignal at exactly the right time.
  //
  // gigId    = the UUID of the gig
  // gigData  = the gig object including all time fields and coords
  // userId   = the UUID of the user who owns the gig
  //
  // IMPORTANT: Always call checkPendingNotifications() right
  // after this function. It is a no-op now, but keeping the
  // call pattern means we do not have to update AddGig if
  // that function ever gets real logic again.
  // -------------------------------------------------------
  const scheduleForGig = useCallback(async (gigId, gigData, userId) => {

    if (!gigId || !userId) {
      console.warn('[useScheduleNotifications] Missing gigId or userId; skipping.')
      return
    }

    setScheduling(true)
    setScheduleError(null)

    try {

      // Load the user's notification preferences from Supabase.
      // We need advance_notice_minutes to calculate warning times,
      // react_subscription_id to confirm notifications will deliver,
      // and home coords for the drive-home calculation.
      const { data: prefs } = await supabase
        .from('notification_preferences')
        .select('advance_notice_minutes, react_subscription_id, home_lat, home_lng')
        .eq('user_id', userId)
        .single()

      const advanceMin = prefs?.advance_notice_minutes || 30
      // default to 30 minutes if the user has not set a preference

      // Warn if no subscription ID exists. Rows will still be written but
      // the Edge Function will skip them because there is no device to send to.
      if (!prefs?.react_subscription_id) {
        console.warn(
          '[useScheduleNotifications] No react_subscription_id found for user',
          userId,
          '-- rows will be written but the Edge Function will skip them until',
          'the user enables notifications in Settings.'
        )
      }

      // Delete all existing rows for this gig before writing new ones.
      // This ensures a time change on an existing gig fully resets the
      // schedule instead of leaving stale rows mixed with new ones.
      const { error: deleteError } = await supabase
        .from('scheduled_notifications')
        .delete()
        .eq('gig_id', gigId)
        // delete WHERE gig_id matches this gig

      if (deleteError) {
        console.error('[useScheduleNotifications] Could not clear old rows:', deleteError.message)
        // not fatal -- we continue and try to insert the new rows anyway
      }

      const timezone    = gigData.timezone || 'America/New_York'
      // fall back to Eastern if the gig has no timezone stored

      const now         = new Date()
      // the current moment in UTC, used to check whether times are in the past

      const staleCutoff = new Date(now.getTime() - STALE_HOURS * 60 * 60 * 1000)
      // any notification time older than this is considered too stale to send
      // STALE_HOURS * 60 * 60 * 1000 converts hours to milliseconds

      const isOtherMode = (gigData.get_ready_location || '').startsWith('other')
      // 'other' mode means the performer gets ready at a separate location
      // and needs a leave_home notification before the venue leave time

      const rows = []
      // the batch of rows we will insert at the end
      // we build the full list first, then do one insert


      // ---- HELPER: toUTC ----
      // Converts a gig time field + optional offset to a UTC Date.
      //
      // timeStr        = a "HH:MM" time string from the gig row
      // offsetMinutes  = shift the base time by this many minutes
      //                  (negative = earlier, e.g. -30 for a 30-min warning)
      //
      // Returns:
      //   a future Date   = schedule it normally
      //   the current Date = time is in the recent past, fire ASAP
      //   null            = time is too stale, drop it
      function toUTC(timeStr, offsetMinutes) {
        if (!timeStr) return null
        // no time field = nothing to schedule

        const base = gigLocalToUTC(gigData.date, timeStr, timezone)
        if (!base) return null
        // gigLocalToUTC returned null = date/time/timezone was invalid

        const adjusted = new Date(base.getTime() + (offsetMinutes || 0) * 60000)
        // offsetMinutes * 60000 converts minutes to milliseconds
        // 0 is the default so a call without an offset just uses the base time

        if (adjusted > now) return adjusted
        // time is in the future -- schedule it normally

        if (adjusted <= staleCutoff) {
          // time is older than STALE_HOURS -- too old to bother sending
          console.log('[useScheduleNotifications] Dropping stale time:', adjusted.toISOString())
          return null
        }

        // Time is in the recent past (within STALE_HOURS).
        // Return the current moment so the cron fires it within 1 minute.
        console.log(
          '[useScheduleNotifications] Past time → immediate delivery:',
          adjusted.toISOString(), '(was', Math.round((now - adjusted) / 60000), 'min ago)'
        )
        return new Date(now.getTime())
      }


      // ---- HELPER: addRow ----
      // Adds one row object to the rows batch array.
      //
      // notifType     = the notification type string (e.g. 'get_ready_now')
      // sendAtDate    = the UTC Date when this notification should fire
      // driveMinutes  = optional drive time in minutes (for head_home rows)
      function addRow(notifType, sendAtDate, driveMinutes) {
        if (!sendAtDate) return
        // null sendAtDate means toUTC dropped this time, so skip it

        const row = {
          user_id:    userId,
          // who this notification belongs to
          gig_id:     gigId,
          // which gig triggered it
          notif_type: notifType,
          // tells the Edge Function what message to send
          send_at:    sendAtDate.toISOString(),
          // the UTC timestamp the cron uses to decide when to fire
          sent:       false,
          // starts as false; the Edge Function sets it to true after delivery
        }

        if (typeof driveMinutes === 'number') {
          row.drive_minutes = driveMinutes
          // only add this field when it was explicitly provided
          // used for head_home rows so the Edge Function can log routing context
        }

        rows.push(row)
        // add this row to the batch
      }


      // ---- STANDARD GET-READY AND TRAVEL ROWS ----

      addRow('get_ready_warning', toUTC(gigData.makeup_start_time, -advanceMin))
      // fires advanceMin minutes before it is time to start getting ready

      addRow('get_ready_now',     toUTC(gigData.makeup_start_time))
      // fires exactly when it is time to start getting ready

      if (isOtherMode) {
        // Only include leave_home rows when the performer is getting
        // ready somewhere other than home (like a venue dressing room)
        addRow('leave_home_warning', toUTC(gigData.leave_home_time, -advanceMin))
        addRow('leave_home_now',     toUTC(gigData.leave_home_time))
      }

      addRow('leave_warning', toUTC(gigData.leave_time, -advanceMin))
      // fires advanceMin minutes before the performer needs to leave for the venue

      addRow('leave_now',     toUTC(gigData.leave_time))
      // fires exactly when the performer needs to leave for the venue


      // ---- PICKUP ROWS ----
      // These only apply when the gig has a pickup_time set.
      // pickup_time is when someone is coming to get the performer,
      // so the performer needs to be ready and at the venue door.

      if (gigData.pickup_time) {
        const pickupTimeStr = gigData.pickup_time.slice(0, 5)
        // .slice(0, 5) = take the first 5 characters = "HH:MM"
        // this normalises values like "20:00:00" to just "20:00"

        addRow('pickup_warning', toUTC(pickupTimeStr, -advanceMin))
        // fires advanceMin minutes before the pickup arrives

        addRow('pickup_now',     toUTC(pickupTimeStr))
        // fires exactly when the pickup arrives

        // head_home rows: tell the performer when to leave the venue
        // to get home before their ride arrives.
        // Requires both venue coordinates and home coordinates
        // so we can calculate the drive time via OSRM.

        const hasVenueCoords = gigData.venue_lat != null && gigData.venue_lng != null
        // != null catches both null and undefined
        const hasHomeCoords  = prefs?.home_lat  != null && prefs?.home_lng  != null

        if (hasVenueCoords && hasHomeCoords) {
          // Both sets of coordinates are present -- fetch drive time
          const driveHomeMin = await fetchDriveMinutes(
            gigData.venue_lat, gigData.venue_lng,
            // FROM: the venue
            prefs.home_lat,    prefs.home_lng
            // TO: the performer's home
          )

          if (driveHomeMin != null) {
            // We have a valid drive time. Schedule the head_home rows.

            addRow(
              'head_home_now',
              toUTC(pickupTimeStr, -driveHomeMin),
              // negative offset = fire driveHomeMin minutes before pickup_time
              driveHomeMin
              // pass drive minutes so the Edge Function can log routing context
            )

            addRow(
              'head_home_warning',
              toUTC(pickupTimeStr, -(driveHomeMin + advanceMin)),
              // fire an additional advanceMin earlier as an early warning
              driveHomeMin
            )

            console.log(
              '[useScheduleNotifications] head_home rows scheduled:',
              `drive home = ${driveHomeMin} min,`,
              `head_home_warning = ${advanceMin} min before that.`
            )

          } else {
            // OSRM call failed or returned no route.
            // Not fatal -- pickup_warning and pickup_now are already queued.
            console.warn(
              '[useScheduleNotifications] Could not fetch drive-home time for gig',
              gigId, '-- head_home rows skipped, pickup rows still scheduled.'
            )
          }

        } else {
          // Missing coordinates -- skip head_home rows silently
          if (!hasVenueCoords) {
            console.log(
              '[useScheduleNotifications] Skipping head_home rows: venue coords missing on gig', gigId
            )
          }
          if (!hasHomeCoords) {
            console.log(
              '[useScheduleNotifications] Skipping head_home rows: home coords missing in prefs for user', userId
            )
          }
          // pickup_warning and pickup_now still get scheduled above
        }
      }


      if (rows.length === 0) {
        console.log(
          '[useScheduleNotifications] No rows to schedule for gig', gigId,
          '(all stale or times missing).'
        )
        return
        // nothing to insert -- stop here
      }

      // Insert all the rows in one batch request
      const { error: insertError } = await supabase
        .from('scheduled_notifications')
        .insert(rows)
        // rows = the full array of row objects we built above

      if (insertError) throw new Error(insertError.message)

      console.log(
        `[useScheduleNotifications] Scheduled ${rows.length} notification(s) for gig ${gigId}`,
        `(${advanceMin} min advance warning):`,
        rows.map(r => `${r.notif_type} @ ${r.send_at}`)
        // log each type and its send time so we can verify the schedule is correct
      )

    } catch (err) {
      console.error('[useScheduleNotifications] Error:', err.message)
      setScheduleError(err.message)
      // the gig was already saved successfully so this is non-fatal
      // we log it but do not re-throw
    } finally {
      setScheduling(false)
      // always turn off the loading flag when done
    }
  }, [])
  // [] = no dependencies -- this callback never needs to be recreated


  return {
    scheduleForGig,
    // (gigId: string, gigData: object, userId: string) => Promise<void>
    // call after every successful gig insert or update

    checkPendingNotifications,
    // () => Promise<number>
    // no-op, returns 0, kept for call-site compatibility

    scheduling,
    // boolean -- true while scheduleForGig is running

    scheduleError,
    // string | null -- last error message from scheduling
  }
}